import { parseCalendarDate } from '@/lib/calendar-date'
import type { DayListRow } from './day-list'

/**
 * The phone's agenda: the month as a list of days that have something on
 * them, with each run of empty days folded into one quiet line. The rows are
 * the day list's (`DayListRow`); this is only the shape of the list.
 */

export interface AgendaDay {
  kind: 'day'
  day: string
  rows: DayListRow[]
}

/** A run of consecutive days with nothing on them, `from` to `to` inclusive. */
export interface AgendaQuiet {
  kind: 'quiet'
  from: string
  to: string
}

export type AgendaEntry = AgendaDay | AgendaQuiet

/** Every day of `month` (`YYYY-MM`) as `YYYY-MM-DD`. */
export function monthDays(month: string): string[] {
  const [year, monthNumber] = month.split('-').map(Number)
  const count = new Date(year, monthNumber, 0).getDate()
  return Array.from(
    { length: count },
    (_, index) => `${month}-${String(index + 1).padStart(2, '0')}`
  )
}

/** The first day of the month after `month` (`YYYY-MM`), as `YYYY-MM-DD`. */
export function nextMonthStart(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number)
  const next = new Date(year, monthNumber, 1)
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`
}

/**
 * Days in order, each with its rows; consecutive days with none become one
 * `quiet` entry, so a quiet week is a line and not seven empty headers.
 */
export function buildAgenda(days: string[], rowsFor: (day: string) => DayListRow[]): AgendaEntry[] {
  const entries: AgendaEntry[] = []
  for (const day of days) {
    const rows = rowsFor(day)
    if (rows.length > 0) {
      entries.push({ kind: 'day', day, rows })
      continue
    }
    const last = entries[entries.length - 1]
    if (last?.kind === 'quiet') last.to = day
    else entries.push({ kind: 'quiet', from: day, to: day })
  }
  return entries
}

/** "Oct 3": a date in a sentence. The year is the month on screen's, so it is left out. */
export function shortDay(day: string): string {
  const date = parseCalendarDate(day)
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : day
}

/** "Oct 3 to Oct 8", or "Oct 3" for a run of one day. */
export function quietSpan(entry: AgendaQuiet): string {
  return entry.from === entry.to
    ? shortDay(entry.from)
    : `${shortDay(entry.from)} to ${shortDay(entry.to)}`
}

/** The header of a day: "Tue, Oct 6" for the eye and "Tuesday, October 6" for a screen reader. */
export function dayHeader(day: string): { short: string; long: string } {
  const date = parseCalendarDate(day)
  if (!date) return { short: day, long: day }
  return {
    short: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    long: date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
  }
}
